import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations } from "../i18n";
import { deepMerge } from "../overrides";
import { getOverrides } from "../api";

const LanguageContext = createContext(null);

const isEnPath = (p) => p === "/en" || p.startsWith("/en/");
const stripEn = (p) => p.replace(/^\/en(?=\/|$)/, "") || "/";

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = isEnPath(location.pathname) ? "en" : "de";

  const [overrides, setOverrides] = useState({ de: {}, en: {} });

  const reloadOverrides = () =>
    getOverrides().then((o) => setOverrides({ de: o.de || {}, en: o.en || {} })).catch(() => {});

  useEffect(() => { reloadOverrides(); }, []);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const t = useMemo(() => deepMerge(translations[lang], overrides[lang] || {}), [lang, overrides]);

  const localizePath = (path) => {
    if (typeof path !== "string" || !path.startsWith("/")) return path;
    if (lang !== "en") return path;
    return path === "/" ? "/en" : `/en${path}`;
  };

  const toggle = () => {
    const dePath = stripEn(location.pathname);
    const target = lang === "de" ? (dePath === "/" ? "/en" : `/en${dePath}`) : dePath;
    navigate(target);
  };

  return (
    <LanguageContext.Provider value={{ lang, t, localizePath, toggle, overrides, setOverrides, reloadOverrides }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
